import { useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import useAuth from 'hooks/auth/use-auth';
import useErrorModal from 'hooks/use-error-modal';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

export function useFavoriteStores() {
  const { user } = useAuth();
  const favStoresEnabled = useFlag(LaunchDarklyFlag.ENABLE_FAVORITE_STORES);
  const showFavoritesByDefaultFlag = useFlag(
    LaunchDarklyFlag.ENABLE_FAVORITE_STORES_DEFAULT_VIEW_IF_USER_IS_LOADED
  );
  const validStoreIds = useMemo(() => {
    const favoriteStores = user?.details?.favoriteStores || [];
    const storeIds = favoriteStores.map(fav => fav.storeId);
    // We need to remove falsy ids that were previously saved from bad sanity data
    return storeIds.filter(i => !!i);
  }, [user]);

  const showFavoritesByDefault = showFavoritesByDefaultFlag && !!validStoreIds.length;

  return {
    favStoresEnabled,
    favoriteStoreIds: validStoreIds,
    showFavoritesByDefault
  };
}

export const useFavoriteStore = (storeId: string) => {
  const { formatMessage } = useIntl();
  const { updateUserFavStores, isAuthenticated } = useAuth();
  const { favStoresEnabled, favoriteStoreIds } = useFavoriteStores();

  const [favStoreLoading, setFavStoreLoading] = useState(false);

  const [ErrorFavoriteStoreDialog, openErrorDialog] = useErrorModal({
    onConfirm: () => null,
    modalAppearanceEventMessage: 'Error: Sign In to Set Favorite Stores'
  });

  const isFavorite = useMemo(() => favoriteStoreIds.includes(storeId), [favoriteStoreIds, storeId]);

  const toggleFavorite = async () => {
    if (!isAuthenticated()) {
      openErrorDialog({
        title: formatMessage({ id: 'errorDialogFavoriteStoreTitle' }),
        message: formatMessage({ id: 'errorDialogFavoriteStoreMessage' })
      });
      return;
    }

    const storeIds = favoriteStoreIds.includes(storeId)
      ? favoriteStoreIds.filter(id => id !== storeId)
      : [storeId, ...favoriteStoreIds];
    // We need to remove falsy ids before we save to clean up bad sanity data
    const validStoreIds = storeIds.filter(i => !!i);

    const storeIdsToSave = validStoreIds.map(id => ({ storeId: id }));
    setFavStoreLoading(true);
    await updateUserFavStores(storeIdsToSave);
    setFavStoreLoading(false);
  };

  return {
    favStoresEnabled,
    toggleFavorite,
    isFavorite,
    ErrorFavoriteStoreDialog,
    favStoreLoading
  };
};
